import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Home, Tv, Wine, Dumbbell } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

const BasementsPage = () => {
  const features = [
    {
      icon: Home,
      title: 'Legal Basement Suites',
      description: 'Complete legal suite development with separate entrance, kitchen, and bathroom. We handle all permits and inspections.',
    },
    {
      icon: Tv,
      title: 'Entertainment Spaces',
      description: 'Custom home theaters, game rooms, and entertainment areas designed for your lifestyle.',
    },
    {
      icon: Wine,
      title: 'Wet Bars & Wine Rooms',
      description: 'Sophisticated wet bars and temperature-controlled wine storage for entertaining.',
    },
    {
      icon: Dumbbell,
      title: 'Home Gyms & Offices',
      description: 'Functional spaces for working out or working from home, with proper flooring and ventilation.',
    },
  ];

  const benefits = [
    'Increase your home value by up to 30%',
    'Generate rental income with a legal suite',
    'Create additional living space for family',
    'Custom designs tailored to your needs',
    'All permits and inspections handled',
    'Warranty on all workmanship',
  ];

  const galleryImages = [
    {
      url: '/hero-basement.png',
      title: 'Modern Basement Living',
      alt: 'Calgary basement renovation with electric fireplace and sectional sofa'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/eas5t004_1000069166.jpg',
      title: 'Contemporary Space',
      alt: 'Calgary basement development with modern fireplace and terrazzo flooring'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/7qdfn1i1_1000069172.jpg',
      title: 'Feature Wall',
      alt: 'Calgary basement feature wall with marble tile and linear fireplace'
    },
  ];

  return (
    <div className="min-h-screen" data-testid="basements-page">
      <Navigation />
      
      {/* Hero Section - AI Generated Calgary Basement */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="/hero-basement.png"
            alt="Calgary basement renovation with electric fireplace and modern living space - Basement finishing Calgary"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0f172a]/60" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
            Our Services
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
            Basement Development
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Transform your unfinished basement into functional, beautiful living space. 
            Calgary's trusted experts for basement renovation and development.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
                Calgary Basement Contractor
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-6">
                Complete Basement Renovations in Calgary
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed">
                Whether you're looking to add a legal basement suite, create an entertainment 
                space, or simply finish your unfinished basement, YYC Custom Basement & Bath 
                delivers quality craftsmanship and reliable timelines.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                As a local Calgary contractor, we understand the unique requirements for 
                basement development in Alberta, including proper moisture control, egress 
                windows, and building code compliance.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {benefits.slice(0, 4).map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#c5a059] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/eas5t004_1000069166.jpg"
                alt="Calgary basement living space with modern fireplace"
                className="w-full h-64 object-cover"
                loading="lazy"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/y82vqawa_1000069194.jpg"
                alt="Custom stone fireplace Calgary basement"
                className="w-full h-64 object-cover mt-8"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-28 bg-[#f8f9fa]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              What We Offer
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
              Basement Development Options
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 border border-slate-100 hover:border-slate-200 transition-all hover:-translate-y-1 hover:shadow-lg"
                data-testid={`basement-feature-${index}`}
              >
                <feature.icon size={32} className="text-[#c5a059] mb-4" />
                <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
                Our Work
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
                Basement Project Gallery
              </h2>
            </div>
            <Link
              to="/gallery"
              className="hidden md:flex items-center gap-2 text-[#0f172a] font-medium hover:text-[#c5a059] transition-colors"
            >
              View All
              <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div key={index} className="group relative overflow-hidden aspect-[4/3]">
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#0f172a]/0 group-hover:bg-[#0f172a]/40 transition-all flex items-end p-6">
                  <span className="text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    {image.title}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to Develop Your Basement?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Get a free estimate for your basement development project. 
            We'll discuss your vision, timeline, and budget.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
            data-testid="basement-cta-button"
          >
            Request Free Estimate
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BasementsPage;
